import { z } from 'zod';
import type { AsyncResult } from '../../types/common';
import { err, ok } from '../../utils/fp-utils';
import type { ApiError, RequestConfig } from './client';

const STRIPE_STATUS_BASE_URL = (() => {
  const configured = (import.meta as any).env?.VITE_STRIPE_STATUS_BASE_URL;
  if (configured) return configured;
  if (typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) {
    return 'http://localhost:9000';
  }
  return '';
})();

const VerifySessionResponseSchema = z.object({
  verified: z.boolean(),
  status: z.enum(['paid', 'unpaid', 'pending', 'invalid']),
  access_granted: z.boolean(),
  access_code: z.string().optional(),
  course_id: z.string().optional(),
  purchase_id: z.string().optional(),
  reason: z.string().optional(),
});

const RedeemAccessCodeResponseSchema = z.object({
  success: z.boolean(),
  access_granted: z.boolean(),
  course_id: z.string().optional(),
  already_redeemed: z.boolean().optional(),
  reason: z.string().optional(),
});

export type VerifySessionResponse = z.infer<typeof VerifySessionResponseSchema>;
export type RedeemAccessCodeResponse = z.infer<typeof RedeemAccessCodeResponseSchema>;

const getBackendBaseUrls = (): string[] => {
  const urls = new Set<string>();

  if (STRIPE_STATUS_BASE_URL) urls.add(STRIPE_STATUS_BASE_URL.replace(/\/$/, ''));
  if (typeof window !== 'undefined') {
    urls.add(window.location.origin.replace(/\/$/, ''));
    if (/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) {
      urls.add('http://localhost:8787');
    }
  }

  return Array.from(urls);
};

const getAuthHeader = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};
  const token = localStorage.getItem('access_token');
  if (!token) return {};

  return {
    Authorization: `Bearer ${token}`,
  };
};

const postToStripeBackend = async <T>(
  paths: ReadonlyArray<string>,
  payload: Record<string, unknown>,
  schema: z.ZodType<T>,
  config?: RequestConfig
): AsyncResult<T, ApiError> => {
  const urls = getBackendBaseUrls();
  let lastError: ApiError = {
    message: `No Stripe backend responded for ${paths.join(', ')}`,
  };

  for (const baseUrl of urls) {
    for (const path of paths) {
      try {
        const response = await fetch(`${baseUrl}${path}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader(),
            ...(config?.headers || {}),
          },
          body: JSON.stringify(payload),
          signal: config?.signal,
        });

        const text = await response.text();
        const body = text ? JSON.parse(text) : {};

        if (!response.ok) {
          lastError = {
            message: body?.reason || body?.error || body?.message || `HTTP ${response.status}`,
            status: response.status,
          };
          continue;
        }

        const parsed = schema.safeParse(body);
        if (!parsed.success) {
          lastError = {
            message: 'Invalid response format from Stripe backend',
            status: response.status,
          };
          continue;
        }

        return ok(parsed.data);
      } catch (error) {
        lastError = {
          message: error instanceof Error ? error.message : `Failed to call ${baseUrl}${path}`,
        };
      }
    }
  }

  return err(lastError);
};

export const paymentsApi = {
  verifySession: (
    sessionId: string,
    options?: {
      courseId?: string;
      userId?: string;
      email?: string;
    },
    config?: RequestConfig
  ): AsyncResult<VerifySessionResponse, ApiError> =>
    postToStripeBackend(
      ['/stripe/verify-session', '/api/payments/stripe/verify-session'],
      {
        session_id: sessionId,
        course_id: options?.courseId,
        user_id: options?.userId,
        email: options?.email,
      },
      VerifySessionResponseSchema,
      config
    ),

  redeemAccessCode: (
    code: string,
    options?: {
      courseId?: string;
      userId?: string;
      email?: string;
    },
    config?: RequestConfig
  ): AsyncResult<RedeemAccessCodeResponse, ApiError> =>
    postToStripeBackend(
      ['/stripe/redeem-access-code', '/api/courses/redeem-access-code'],
      {
        code,
        course_id: options?.courseId,
        user_id: options?.userId,
        email: options?.email,
      },
      RedeemAccessCodeResponseSchema,
      config
    ),
};
